import type { QueryOperationName } from '@portal/rocket-api-client';

import { i18n } from '@shared/i18n';

/**
 * Error copy for failed queries, keyed by generated operation name. The client
 * injects the resolved string as `meta.errorMessage`, which becomes the prefix
 * of the message ErrorHandler shows. Modules append their entries here as they
 * migrate to the generated client, e.g.
 *
 *   listSites: () => i18n.t('data_fetching_error.could_not_list_sites'),
 */
export const ERROR_MESSAGE_MAP: Partial<Record<QueryOperationName, () => string>> = {
  listAllAvailableSiteLocations: () => i18n.t('data_fetching_error.could_not_get_site_locations'),
  listBillingEmails: () => i18n.t('data_fetching_error.could_not_list_emails'),
  findBillingEmail: () => i18n.t('data_fetching_error.could_not_get_email'),
  listSiteFtpAccounts: () => i18n.t('data_fetching_error.could_not_list_ftp_accounts'),
  listSiteDomains: () => i18n.t('data_fetching_error.could_not_list_site_domains'),
  listSiteSshKeys: () => i18n.t('data_fetching_error.could_not_list_ssh_keys'),
  findSiteSshKey: () => i18n.t('data_fetching_error.could_not_get_ssh_info'),
  listSiteThemes: () => i18n.t('data_fetching_error.could_not_list_themes'),
  searchSiteThemes: () => i18n.t('data_fetching_error.could_not_list_themes'),
  findSitePasswordProtection: () =>
    i18n.t('data_fetching_error.could_not_get_password_protection_status'),
  listSitePasswordProtectionUsers: () =>
    i18n.t('data_fetching_error.could_not_get_password_protection_users'),
  listSitePlugins: () => i18n.t('data_fetching_error.could_not_list_plugins'),
  searchSitePlugins: () => i18n.t('data_fetching_error.could_not_search_plugins'),
  listSiteFeaturedPlugins: () => i18n.t('data_fetching_error.could_not_list_featured_plugins'),
  listSiteCloudBackups: () => i18n.t('data_fetching_error.could_not_list_cloud_backups'),
  listAutomatedSiteBackups: () => i18n.t('data_fetching_error.could_not_list_automated_backups'),
  listAccounts: () => i18n.t('data_fetching_error.could_not_list_accounts'),
  listMyAccountInvites: () => i18n.t('data_fetching_error.could_not_list_account_invites'),
  listSiteUsers: () => i18n.t('data_fetching_error.could_not_list_site_users'),
  acceptSiteUserInviteWhileLoggedIn: () => i18n.t('data_fetching_error.could_not_accept_invite'),
  checkSiteMaindomainStatus: () => i18n.t('data_fetching_error.could_not_get_maindomain_status'),
  reportOnSiteCdnRequests: () => i18n.t('data_fetching_error.could_not_get_cdn_data'),
  reportOnSiteCdnCacheStatus: () => i18n.t('data_fetching_error.could_not_get_cdn_data'),
  reportOnSiteWafFirewallEventsDeprecated: () =>
    i18n.t('data_fetching_error.could_not_get_waf_data'),
  reportOnSiteWafEventsServicesDeprecated: () =>
    i18n.t('data_fetching_error.could_not_get_waf_data'),
  reportOnSiteWafEventsSource: () => i18n.t('data_fetching_error.could_not_get_waf_data'),
  reportOnSiteWafEventlist: () => i18n.t('data_fetching_error.could_not_get_waf_data'),
  listSiteActivityEvents: () => i18n.t('data_fetching_error.could_not_get_activity_log'),
  listSiteAccessLogs: () => i18n.t('data_fetching_error.could_not_list_access_logs'),
  reportOnSiteBandwidthUsage: () => i18n.t('data_fetching_error.could_not_get_reporting_data'),
  reportOnSiteTotalRequests: () => i18n.t('data_fetching_error.could_not_get_reporting_data'),
  reportOnSiteCdnRequestVolumeBySource: () =>
    i18n.t('data_fetching_error.could_not_get_reporting_data'),
  reportOnSiteBandwidthTopUsage: () => i18n.t('data_fetching_error.could_not_get_reporting_data'),
  listSiteTemplates: () => i18n.t('data_fetching_error.could_not_list_site_templates'),
  findSiteTemplate: () => i18n.t('data_fetching_error.could_not_get_site_template'),
  listBillingPaymentMethods: () => i18n.t('data_fetching_error.could_not_list_payment_methods'),
  listBillingInvoices: () => i18n.t('data_fetching_error.could_not_list_invoices'),
  findBillingInvoice: () => i18n.t('data_fetching_error.could_not_get_billing_invoice_data'),
  listBillingProducts: () => i18n.t('data_fetching_error.could_not_list_billing_products'),
  listBillingAddresses: () => i18n.t('data_fetching_error.could_not_list_billing_addresses'),
  findAccountMe: () => i18n.t('data_fetching_error.could_not_get_user_profile'),
  findAccountHostingPlan: () => i18n.t('data_fetching_error.could_not_get_hosting_plan_data'),
  listAccountVisitors: () => i18n.t('data_fetching_error.could_not_get_account_visitors'),
  listAccountUsers: () => i18n.t('data_fetching_error.could_not_list_account_users'),
  listAccountInvites: () => i18n.t('data_fetching_error.could_not_list_account_invites'),
  listSites: () => i18n.t('data_fetching_error.could_not_list_sites'),
  listSiteUserSites: () => i18n.t('data_fetching_error.could_not_list_sites'),
  findSite: () => i18n.t('data_fetching_error.could_not_get_site_details'),
  findSiteSettings: () => i18n.t('data_fetching_error.could_not_get_site_settings'),
  findSiteSettingsSchema: () => i18n.t('data_fetching_error.could_not_get_settings_schema'),
  findSiteMaindomainEdgeSettings: () => i18n.t('data_fetching_error.could_not_get_edge_settings'),
  listSiteFileManagerFiles: () => i18n.t('data_fetching_error.could_not_list_site_files'),
  findSiteWpStatus: () => i18n.t('data_fetching_error.could_not_get_site_wp_status'),
};
